"use client";

import React, { useEffect, useRef, useState } from "react";
import { Maximize2 } from "lucide-react";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTrigger, DialogClose } from "@/components/ui/dialog";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const galleryItems = [{
  id: 1,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660511/img/sensu_candlelit_suite.png",
  caption: "Svit svíček",
  ratio: 4 / 3
}, {
  id: 2,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660511/img/sensu_silk_textures.png",
  caption: "Dotek hedvábí",
  ratio: 1 / 1
}, {
  id: 3,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660511/img/sensu_lounge_ambience.png",
  caption: "Intimní zóna",
  ratio: 3 / 4
}, {
  id: 4,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660511/img/sensu_luxury_bath.png",
  caption: "Očista smyslů",
  ratio: 16 / 9
}, {
  id: 5,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660511/img/sensu_masseuse_detail.png",
  caption: "Umění doteku",
  ratio: 4 / 5
}, {
  id: 6,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660511/img/sensu_champagne_welcome.png",
  caption: "Uvítací rituál",
  ratio: 1 / 1
}, {
  id: 7,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660511/img/sensu_private_corridor.png",
  caption: "Cesta k uvolnění",
  ratio: 4 / 3
}, {
  id: 8,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660511/img/sensu_soft_lighting.png",
  caption: "Noční klid",
  ratio: 3 / 4
}];
export default function VibrantMoments() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll();
  const {
    ref: galleryRef,
    isVisible: isGalleryVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="223917" id="atmosféra-salónu" className="bg-background text-foreground py-24 px-4 sm:px-6 lg:px-8 overflow-hidden relative">
      {}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-primary/5 via-background to-background pointer-events-none" />

      <div className="max-w-7xl mx-auto relative z-10">
        {}
        <div ref={headerRef} className={`text-center mb-16 transition-all duration-1000 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          <Badge variant="outline" className="mb-4 text-primary border-primary/30 font-default uppercase tracking-widest">
            Exkluzivita
          </Badge>
          <h2 className="text-4xl md:text-6xl lg:text-7xl font-default font-light uppercase tracking-widest text-foreground mb-6 drop-shadow-sm">
            Atmosféra <span className="text-primary">Sensu</span>
          </h2>
          <p className="text-muted-foreground text-lg md:text-xl max-w-2xl mx-auto font-default">
            Vstupte do světa, kde se čas zastaví. Objevte prostor navržený pro 
            absolutní diskrétnost, luxus a probuzení vašich smyslů.
          </p>
        </div>

        {}
        <div ref={galleryRef} className={`columns-1 sm:columns-2 lg:columns-3 gap-4 sm:gap-6 space-y-4 sm:space-y-6 transition-all duration-1000 delay-200 ease-out ${isGalleryVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          {galleryItems.map((item, i) => <div key={item.id} data-index={i} className="break-inside-avoid relative group">
              <Dialog>
                <DialogTrigger className="w-full block overflow-hidden rounded-xl bg-muted outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background cursor-zoom-in shadow-md hover:shadow-2xl transition-shadow duration-500">
                  <AspectRatio ratio={item.ratio}>
                    <img src={item.src} alt={item.caption} className="h-full w-full object-cover transition-transform duration-1000 ease-out group-hover:scale-110" loading="lazy" />
                    {}
                    <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/10 to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />

                    {}
                    <div className="absolute inset-0 p-6 flex flex-col justify-end items-start opacity-0 transition-all duration-500 group-hover:opacity-100">
                      <div className="transform translate-y-4 group-hover:translate-y-0 transition-transform duration-500 ease-out">
                        <Badge variant="secondary" className="font-default uppercase tracking-widest text-xs sm:text-sm shadow-lg mb-2 bg-background/80 backdrop-blur-md border-primary/20">
                          {item.caption}
                        </Badge>
                      </div>
                    </div>

                    {}
                    <div className="absolute top-4 right-4 bg-background/40 backdrop-blur-md p-2.5 rounded-full opacity-0 transform scale-75 transition-all duration-500 group-hover:opacity-100 group-hover:scale-100 border border-primary/20">
                      <Maximize2 className="size-4 text-primary" />
                    </div>
                  </AspectRatio>
                </DialogTrigger>

                {}
                <DialogContent className="max-w-5xl bg-background/95 backdrop-blur-2xl border-border shadow-4xl p-1 sm:p-2" showCloseButton={false}>
                  <div className="relative w-full overflow-hidden rounded-lg bg-muted flex items-center justify-center min-h-[50vh]">
                    <img src={item.src} alt={item.caption} className="w-full h-auto max-h-[85vh] object-contain" />
                    <DialogClose className="absolute top-4 right-4 bg-background/50 hover:bg-background/80 text-foreground p-2 rounded-full backdrop-blur-md transition-colors outline-none focus-visible:ring-2 focus-visible:ring-primary border border-primary/20">
                      <span className="sr-only">Zavřít</span>
                      <Maximize2 className="size-5 rotate-45" />
                    </DialogClose>
                    <div className="absolute bottom-6 left-1/2 -translate-x-1/2">
                      <Badge variant="default" className="font-default uppercase tracking-widest text-sm px-6 py-2 shadow-2xl bg-primary text-primary-foreground">
                        {item.caption}
                      </Badge>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>)}
        </div>

        {}
        <div className="mt-16 flex justify-center">
          
        </div>
      </div>
    </section>;
}